import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from "@/components/ui/confirm";
import { getCompanies } from '@/services/companyService';
import { WebSearchStatus } from '@/services/companyWebSearchService';
import { PendingCompanyRequestAlert } from '@/components/PendingCompanyRequestAlert';
import CompanyHeader from '@/components/companies/CompanyHeader';
import CompanyList from '@/components/companies/CompanyList';
import CompanySearch from '@/components/companies/CompanySearch';
import CompanyAccessInfo from '@/components/companies/CompanyAccessInfo';

const Companies = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const isAdmin = profile?.role === 'admin';

  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [webSearchStatuses, setWebSearchStatuses] = useState<Record<string, WebSearchStatus>>({});

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setIsLoading(true);
        const companiesData = await getCompanies();
        setCompanies(companiesData);
        
        // Web search status is now included in company data
        const statusMap: Record<string, any> = {};
        companiesData.forEach(company => {
          statusMap[company.id] = {
            status: company.web_search_status || 'pending',
            data: company.web_search_data || null,
            updated_at: company.web_search_updated_at || null,
            queue_time: company.web_search_queue_time || null
          };
        });
        setWebSearchStatuses(statusMap);
      } catch (error) {
        console.error('Error fetching companies:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load companies"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompanies();
  }, [toast]);

  const filteredCompanies = companies.filter(company =>
    company.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
      <ConfirmDialog />
      
      <div className="mb-6 sm:mb-8">
        <CompanyHeader isAdmin={isAdmin} />
      </div>
      
      <PendingCompanyRequestAlert />
      <CompanyAccessInfo 
        isAdmin={isAdmin} 
        noResults={filteredCompanies.length === 0} 
        isLoading={isLoading} 
      />

      <Card className="shadow-lg">
        <CardHeader className="p-4 sm:p-5 md:p-6">
          <CardTitle className="text-lg sm:text-xl md:text-2xl">
            {isAdmin ? 'Company Directory' : 'Your Companies'}
          </CardTitle>
          <CardDescription className="text-sm">
            {isAdmin 
              ? 'Manage all companies in the system' 
              : 'Companies you have access to'
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 sm:p-5 md:p-6 pt-0">
          <div className="mb-4 sm:mb-6">
            <CompanySearch 
              searchQuery={searchQuery} 
              setSearchQuery={setSearchQuery} 
            />
          </div>
          <CompanyList 
            companies={filteredCompanies} 
            isAdmin={isAdmin} 
            webSearchStatuses={webSearchStatuses}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default Companies;
